import {
  Brand,
  Practice,
  PracticeSetupWizard,
  Task,
} from '@principle-theorem/principle-core';
import {
  ChatStatus,
  ConversationStatus,
  type IAppointmentRequest,
  type IStaffer,
  type IStafferSettings,
  PracticeSetupStepStatus,
  PracticeSetupWizardStatus,
  TaskStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  count,
  Firestore,
  getError,
  isChanged$,
  isSameRef,
  limit,
  multiFilter,
  multiSwitchMap,
  orderBy,
  toMomentTz,
  toTimestamp,
  undeletedFilter,
  undeletedQuery,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, uniqWith } from 'lodash';
import { combineLatest, from, type Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { type ISideBarItemScope } from './sidebar-items';

const AWAITING_DEPOSIT_BADGE_LIMIT = 10;

export class SidebarBadges {
  scope?: Badgeworthy;

  constructor(private _scope: ISideBarItemScope) {
    this.scope = this._scope.practice
      ? {
          appointmentRequests$: Practice.getAppointmentRequests$(
            this._scope.practice
          ),
        }
      : this._scope.brand
        ? {
            appointmentRequests$: Brand.getAppointmentRequests$(
              this._scope.brand
            ),
          }
        : undefined;
  }

  /**
   * Counts both the appointment-requests queue and the awaiting-deposit queue,
   * because both now live on the Appointment Requests page. The deposit query
   * asks for a fraction of the full work queue because the sidebar collapses
   * anything at or above `AWAITING_DEPOSIT_BADGE_LIMIT` into `10+` regardless.
   */
  getAppointmentRequestsBadge(): Observable<number> {
    if (!this.scope) {
      return of(0);
    }
    return combineLatest([
      this.scope.appointmentRequests$.pipe(count()),
      this._appointmentsAwaitingDepositBadge$(),
    ]).pipe(map(([requests, deposits]) => requests + deposits));
  }

  getFollowUpsBadge(): Observable<number> {
    const practice = this._scope.practice;
    if (!practice) {
      return of(0);
    }
    return Practice.getAppointmentsWithExistingFollowUps$(practice, 10).pipe(
      map((appointments) => appointments.length)
    );
  }

  getConversationsBadge(): Observable<number> {
    const practice = this._scope.practice;
    const brand = this._scope.brand;
    const staffer = this._scope.staffer;
    if (!brand || !practice || !staffer) {
      return of(0);
    }

    const userPractices$ = this._scope.userPractices$ || of([]);
    const showAllPractices$ = this._stafferSettings$(staffer).pipe(
      map((settings) => settings?.conversations?.showAllPractices ?? false),
      isChanged$()
    );

    return combineLatest([userPractices$, showAllPractices$]).pipe(
      switchMap(([userPractices, showAllPractices]) => {
        if (!showAllPractices || !userPractices.length) {
          return Firestore.query$(
            Brand.smsConversationCol(brand),
            where('practiceRef', '==', practice.ref),
            where('status', '==', ConversationStatus.Open),
            orderBy('lastMessageAt', 'desc'),
            limit(10)
          );
        }
        return Firestore.query$(
          Brand.smsConversationCol(brand),
          where(
            'practiceRef',
            'in',
            userPractices.map((userPractice) => userPractice.ref)
          ),
          where('status', '==', ConversationStatus.Open),
          orderBy('lastMessageAt', 'desc'),
          limit(10)
        );
      }),
      multiSwitchMap((conversation) =>
        conversation.lastMessage
          ? Firestore.doc$(conversation.lastMessage)
          : of(undefined)
      ),
      map(compact),
      multiFilter(
        (message) =>
          !message.readBy.find((readBy) =>
            isSameRef(readBy, this._scope.staffer)
          )
      ),
      count()
    );
  }

  getChatsBadge(): Observable<number> {
    const practice = this._scope.practice;
    const brand = this._scope.brand;
    const staffer = this._scope.staffer;
    if (!brand || !practice || !staffer) {
      return of(0);
    }

    const userPractices$ = this._scope.userPractices$ || of([]);
    const showAllPractices$ = this._stafferSettings$(staffer).pipe(
      map((settings) => settings?.chats?.showAllPractices ?? false),
      isChanged$()
    );

    return combineLatest([userPractices$, showAllPractices$]).pipe(
      switchMap(([userPractices, showAllPractices]) => {
        if (!showAllPractices || !userPractices.length) {
          return Firestore.query$(
            Brand.chatCol(brand),
            where('practiceRef', '==', practice.ref),
            where('participants', 'array-contains', staffer.ref),
            where('status', '==', ChatStatus.Open),
            orderBy('lastMessageAt', 'desc'),
            limit(10)
          );
        }
        return Firestore.query$(
          Brand.chatCol(brand),
          where(
            'practiceRef',
            'in',
            userPractices.map((userPractice) => userPractice.ref)
          ),
          where('participants', 'array-contains', staffer.ref),
          where('status', '==', ChatStatus.Open),
          orderBy('lastMessageAt', 'desc'),
          limit(10)
        );
      }),
      multiSwitchMap((conversation) =>
        conversation.lastMessage
          ? Firestore.doc$(conversation.lastMessage)
          : of(undefined)
      ),
      map(compact),
      multiFilter(
        (message) =>
          !message.readBy.find((readBy) =>
            isSameRef(readBy, this._scope.staffer)
          )
      ),
      count()
    );
  }

  getTasksBadge(): Observable<number> {
    if (!this._scope.staffer || !this.scope) {
      return of(0);
    }
    const staffer: WithRef<IStaffer> = this._scope.staffer;

    if (this._scope.practice) {
      const today = toMomentTz(
        toTimestamp(),
        this._scope.practice.settings.timezone
      ).startOf('day');

      return combineLatest([
        Firestore.query$(
          undeletedQuery(Task.col(this._scope.practice)),
          where('assignedUser.ref', '==', staffer.ref),
          where('status', '==', TaskStatus.Open),
          where('visibleFrom', '<=', toTimestamp(today)),
          limit(10)
        ),
        staffer.teams.length
          ? Firestore.query$(
              undeletedQuery(Task.col(this._scope.practice)),
              where(
                'assignedTeam.ref',
                'in',
                staffer.teams.map((team) => team.ref)
              ),
              where('status', '==', TaskStatus.Open),
              where('visibleFrom', '<=', toTimestamp(today)),
              limit(10)
            )
          : of([]),
      ]).pipe(
        map(([tasks, teamTasks]) =>
          uniqWith([...tasks, ...teamTasks], isSameRef)
        ),
        count()
      );
    }

    return of(0);
  }

  getReferralsBadge(): Observable<number> {
    const brand = this._scope.brand;
    const staffer = this._scope.staffer;

    if (!brand || !staffer) {
      return of(0);
    }

    return Firestore.query$(
      Brand.referralCol(brand),
      where('deleted', '==', false),
      where('unreadBy', 'array-contains', staffer.ref),
      orderBy('updatedAt', 'desc'),
      limit(10)
    ).pipe(map((unreadReferrals) => unreadReferrals.length));
  }

  getMarketingCampaignBadges(): Observable<number> {
    if (!this._scope.brand) {
      return of(0);
    }
    return combineLatest([
      Brand.marketingAlerts$(this._scope.brand).pipe(
        map((alerts) => alerts.length)
      ),
      Brand.unacknowledgedPaymentAlerts$(this._scope.brand).pipe(
        map((entries) => entries.length)
      ),
    ]).pipe(map(([runAlerts, entryAlerts]) => runAlerts + entryAlerts));
  }

  getSetupWizardBadge(): Observable<string | undefined> {
    const practice = this._scope.practice;
    if (!practice) {
      return of('0%');
    }

    return from(PracticeSetupWizard.getByPractice(practice)).pipe(
      switchMap((wizardDoc) => {
        if (!wizardDoc) {
          return of('0%');
        }
        return Firestore.doc$(wizardDoc.ref).pipe(
          switchMap((wizard) => {
            if (
              wizard.status === PracticeSetupWizardStatus.Complete &&
              wizard.dismissedAt
            ) {
              return of(undefined);
            }
            return Firestore.query$(PracticeSetupWizard.stepCol(wizard)).pipe(
              multiFilter(undeletedFilter),
              map((steps) => {
                if (!steps.length) {
                  return '0%';
                }
                const totalCount = steps.length;
                const completeCount = steps.filter(
                  (step) => step.status === PracticeSetupStepStatus.Complete
                ).length;
                const percent = Math.round((completeCount / totalCount) * 100);
                if (percent === 100) {
                  return '✓';
                }
                return `${percent}%`;
              }),
              catchError(() => of('0%'))
            );
          }),
          catchError(() => of('0%'))
        );
      }),
      catchError(() => of('0%'))
    );
  }

  /**
   * Falls back to zero on failure rather than erroring. This count is combined
   * with the appointment requests count, so an unhealthy deposit query would
   * otherwise take the whole badge down with it and hide a requests queue that
   * is still perfectly readable.
   */
  private _appointmentsAwaitingDepositBadge$(): Observable<number> {
    const practice = this._scope.practice;
    if (!practice) {
      return of(0);
    }
    const windowStart = Practice.awaitingDepositWindowStart();
    return Practice.getAppointmentsAwaitingDeposit$(
      practice,
      windowStart,
      AWAITING_DEPOSIT_BADGE_LIMIT
    ).pipe(
      multiFilter((appointment) =>
        Practice.isAwaitingDeposit(appointment, windowStart)
      ),
      map((appointments) => appointments.length),
      catchError((error: unknown) => {
        void this._scope.logger.log(
          'ERROR',
          'Sidebar badge could not count appointments awaiting a deposit',
          { practice: practice.ref.path, error: getError(error) }
        );
        return of(0);
      })
    );
  }

  private _stafferSettings$(
    staffer: WithRef<IStaffer>
  ): Observable<Partial<IStafferSettings> | undefined> {
    return this._scope.stafferSettings$ ?? of(staffer.settings);
  }
}

export type Badgeworthy = IHasAppointmentRequests;

interface IHasAppointmentRequests {
  appointmentRequests$: Observable<WithRef<IAppointmentRequest>[]>;
}
